import { Container, Sprite, type Texture } from "pixi.js";
import { applyPlacement, keepInsideX, resolveResponsive } from "@slot/presentation";
import type { Placement, Responsive, ViewportStore } from "@slot/presentation";

/** The logo's spot for one shape. */
export interface GameLogoPlacement extends Placement {
  /** Nudge a spot whose authored box pokes outside the composition box back inside on X (the prefab's keep-visible rule). */
  keepInsideX?: boolean;
}

/** A title's in-game logo (`GameSceneHandle.logo`): its authored sizes and its spot per shape. */
export interface GameLogoSpec {
  /** The authored box `keepInsideX` measures (design units). */
  box: { width: number; height: number };
  /** The sprite's authored size (design units). */
  sprite: { width: number; height: number };
  /** Its spot per shape, in composition-root space (design units, y-down). */
  placement: Responsive<GameLogoPlacement>;
}

/**
 * The title's in-game logo (`GameSceneHandle.logo`), the LOCAL boot-assets `logo` texture (not the
 * operator bundle's: the layer is not operator-branded), placed per shape inside the composition root.
 */
export class BrandedIconView extends Container {
  private readonly iconRoot: Container;
  private readonly unsubscribe: () => void;

  public constructor(input: {
    logo: Texture | null;
    spec: GameLogoSpec;
    viewport: ViewportStore;
    /** The composition-root box the layer is stretched to (the desktop visible bounds). */
    bounds: { width: number; height: number };
  }) {
    super({ label: "BrandedIconLayer" });
    const { spec, bounds } = input;
    this.iconRoot = new Container({ label: "IconRoot" });
    if (input.logo) {
      const sprite = new Sprite(input.logo);
      sprite.label = "Sprite";
      sprite.anchor.set(0.5);
      sprite.width = spec.sprite.width;
      sprite.height = spec.sprite.height;
      this.iconRoot.addChild(sprite);
    }
    this.addChild(this.iconRoot);
    this.unsubscribe = input.viewport.subscribe((v) => {
      applyPlacement(this.iconRoot, spec.placement, v.shape);
      if (resolveResponsive(spec.placement, v.shape).keepInsideX) keepInsideX(this.iconRoot, spec.box, { x: -bounds.width / 2, y: -bounds.height / 2, ...bounds });
    });
  }

  public dispose(): void {
    this.unsubscribe();
    this.destroy({ children: true });
  }
}
